/* ==========================================================================
   Mycube — Unified CTA button system (markup normaliser)
   --------------------------------------------------------------------------
   Every marketing CTA in this theme was authored by hand, so they arrive in
   half a dozen shapes: bare text nodes, <span> labels, inline <svg aria-hidden="true" focusable="false"> arrows at
   16/21/45/50/51/52/76px, arrow "boxes" built as <div>s, and arrow boxes built
   as <rect> elements inside the SVG.

   This script rewrites all of them into the single shape that
   assets/theme-cta.css styles:

     <a class="<originalClass> mc-cta">
       <span class="mcCtaLabel">Label</span>
       <span class="mcCtaArrow" aria-hidden="true"><svg aria-hidden="true" focusable="false">…</svg></span>
     </a>

   Colour and layout come from CSS, so the arrow box is a real element with a
   background rather than an SVG <rect> — that is what lets it animate.

   To bring another CTA into the system, add its selector to CTA_SELECTORS.
   Deliberately excluded: in-card text links with small arrows (.articleCardButton,
   .collectionV2CardLink, .dashboardButton, .accDispTopLeftLink, .prodDispTopLink,
   .recommendedBottomCardLink and friends), add-to-cart / checkout, filters,
   accordions, tabs, swiper navigation and form submits.
   ========================================================================== */

(function () {
    'use strict';

    /* ---------------------------------------------------------- tiers

       PRIMARY - solid blue bar. The main action of its view.

       LINK - label, hairline underline and arrow, matching the Shop Safes
       nav text links. Secondary and exploratory CTAs: things you offer, not
       things you are asking for. Before tiering, a "download the manual" on
       a support page shouted exactly as loudly as "shop all safes".

       Moving a selector between the two lists is the whole change - the
       markup normalisation below is identical for both.
       ------------------------------------------------------------------ */

    var PRIMARY_SELECTORS = [
        /* header drawer - the original reference design */
        '#sliderContentNavGroupBottomButton',
        /* home + hero */
        '.heroLink',
        '.heroV2ButtonsButton',
        '.homeChooseRightContainerLink',
        '.homeShopDisplayButtonsButton',
        '.homeUsefulFeaturesCta',
        '.rotatingButton',
        '.fbspot-btn',
        '.quizPortalButton',
        /* collection */
        '.compareItemButtonLink',
        /* product - the accessory cross-sell add button, NOT add-to-cart
           (.productV2PurchaseAtc is deliberately outside this system) */
        '.productV2AddsGroupItemDetailsContentButton',
        /* safe finder (markup is generated by safe-finder-app.js) */
        '.sf-btn',
        /* one-off pages */
        '#notFoundButton',
        '#giftCardShop'
    ].join(',');

    /* Legacy markup that already declared itself secondary. Without this the
       tier is silently lost: normalise() would style it as a primary and the
       only trace of the intent would be the stray outline its own CSS draws
       on the inner label. */
    var SECONDARY_SELECTORS = [
        /* safe finder - the "Email results" submit beside the primary match CTA */
        '.sf-btn--secondary'
    ].join(',');

    var LINK_SELECTORS = [
        /* home */
        '.homeContactContentButton',
        /* collection */
        '.collectionHeadButton',
        '.safeCompTopExplore',
        /* support / content pages */
        '.supportContentFinalItemButton',
        '.termsContentContactCardsItemButton'
    ].join(',');

    var CTA_SELECTORS = PRIMARY_SELECTORS + ',' + LINK_SELECTORS;

    /* Legacy arrow containers that must be discarded before we append ours. */
    var LEGACY_ARROWS = [
        '.mcCtaArrow',
        '.homeChooseRightContainerArrow',
        '.homeShopDisplayButtonsButtonArrow',
        '.fbspot-btn-arrow',
        '.sf-btn-sq'
    ].join(',');

    /* Same two paths as snippets/mc-arrow.liquid. This cannot render Liquid,
       so the geometry lives in both places - keep them in step. */
    var ARROW_SVG =
        '<svg viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg" ' +
        'aria-hidden="true" focusable="false">' +
        '<path d="M10.1744 2.1665L18.2102 10.4998L10.1744 18.8332" stroke="currentColor" stroke-width="1.3"/>' +
        '<path d="M2.58331 10.4995L18.0809 10.4995" stroke="currentColor" stroke-width="1.3"/>' +
        '</svg>';

    function buildArrow() {
        var arrow = document.createElement('span');
        arrow.className = 'mcCtaArrow';
        arrow.setAttribute('aria-hidden', 'true');
        arrow.innerHTML = ARROW_SVG;
        return arrow;
    }

    /* Pick out an existing label wrapper, or make one from whatever is left. */
    function resolveLabel(el) {
        var existing = el.querySelector(':scope > .mcCtaLabel');
        if (existing) {
            return existing;
        }

        var wrappers = [];
        for (var i = 0; i < el.children.length; i++) {
            var child = el.children[i];
            if ((child.tagName === 'SPAN' || child.tagName === 'DIV') && child.textContent.trim()) {
                wrappers.push(child);
            }
        }

        /* Exactly one text-bearing wrapper: reuse it, keep its own classes. */
        if (wrappers.length === 1 && wrappers[0].textContent.trim() === el.textContent.trim()) {
            wrappers[0].classList.add('mcCtaLabel');
            return wrappers[0];
        }

        /* Otherwise move everything that is left into a fresh span. */
        var label = document.createElement('span');
        label.className = 'mcCtaLabel';
        while (el.firstChild) {
            label.appendChild(el.firstChild);
        }
        el.appendChild(label);
        return label;
    }

    function normalise(el) {
        if (!el || el.dataset.mcCta === 'done') {
            return;
        }
        el.dataset.mcCta = 'done';

        /* 1 — strip every legacy arrow: wrapper elements first, then any
               direct-child <svg aria-hidden="true" focusable="false"> or arrow <img> (this also removes the old
               black <rect> boxes, which we now draw in CSS). */
        el.querySelectorAll(LEGACY_ARROWS).forEach(function (node) {
            node.remove();
        });
        Array.prototype.slice.call(el.children).forEach(function (child) {
            var tag = child.tagName.toLowerCase();
            if (tag === 'svg' || (tag === 'img' && !child.textContent.trim())) {
                child.remove();
            }
        });

        /* 2 — normalise the label. */
        var label = resolveLabel(el);

        /* 3 — collapse the whitespace Liquid leaves behind, so the label does
               not inherit stray newlines as flex content. */
        if (label.children.length === 0) {
            label.textContent = label.textContent.replace(/\s+/g, ' ').trim();
        }

        /* 4 — append the canonical arrow and opt in to the styles. */
        el.appendChild(buildArrow());
        el.classList.add('mc-cta');

        /* 5 — tier. Everything above is identical for both; only the class
               differs, and the CSS does the rest. */
        if (el.matches(LINK_SELECTORS)) {
            el.classList.add('mc-cta--link');
        } else if (el.matches(SECONDARY_SELECTORS)) {
            el.classList.add('mc-cta--secondary');
        }
    }

    function run(root) {
        var scope = root && root.querySelectorAll ? root : document;

        if (scope !== document && scope.matches && scope.matches(CTA_SELECTORS)) {
            normalise(scope);
        }
        scope.querySelectorAll(CTA_SELECTORS).forEach(normalise);
    }

    function init() {
        run(document);

        /* Safe Finder and the mini-cart inject CTAs after load, and the theme
           editor re-renders sections in place, so keep watching. */
        if (typeof MutationObserver === 'function') {
            var pending = false;
            new MutationObserver(function (records) {
                if (pending) {
                    return;
                }
                for (var i = 0; i < records.length; i++) {
                    if (records[i].addedNodes.length) {
                        pending = true;
                        requestAnimationFrame(function () {
                            pending = false;
                            run(document);
                        });
                        return;
                    }
                }
            }).observe(document.documentElement, { childList: true, subtree: true });
        }
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

    document.addEventListener('shopify:section:load', function (event) {
        run(event.target || document);
    });
}());
